<template lang="pug">
b-card
  validation-observer(ref="form", tag="form")
    b-form
      b-row
        //- Main entity: Colaborador (worker) or Proveedor (free text)
        b-col(md="6")
          b-form-group(:label="$t(config.mainFieldLabel)", label-for="mainField")
            validation-provider(:name="$t(config.mainFieldLabel)", rules="required", v-slot="{ errors }")
              b-form-input#mainField(
                v-if="config.mainFieldKind === 'text'",
                v-model="data.nombre",
                trim,
                :placeholder="$t(config.mainFieldPlaceholder)"
              )
              v-select(
                v-else,
                v-model="data.trabajador",
                :options="workersTotal",
                :reduce="worker => worker.value",
                :selectable="worker => !worker.deleted",
                label="title",
                :placeholder="$t(config.mainFieldPlaceholder)"
              )
              small(class="text-danger") {{ errors[0] }}

        //- Institución (Lobby only): persona u organismo externo
        b-col(v-if="config.showInstitucion", md="6")
          b-form-group(:label="$t('declaration.institucion')", label-for="institucion")
            b-form-input#institucion(v-model="data.institucion", trim, :placeholder="$t('declaration.placeholder_institucion')")

        //- Empresa (Lobby: right after Institución)
        b-col(v-if="config.showInstitucion", md="6")
          b-form-group(:label="$t('declaration.empresa')", label-for="empresa")
            v-select(
              v-model="data.empresas",
              :options="companies",
              :reduce="company => company.value",
              label="title",
              multiple,
              :placeholder="$t('declaration.placeholder_empresa')"
            )

        //- Área
        b-col(md="6")
          b-form-group(:label="$t('declaration.area')", label-for="area")
            v-select(
              v-model="data.area",
              :options="locations",
              :reduce="location => location.value",
              label="title",
              :placeholder="$t('declaration.placeholder_area')"
            )

        //- Tipo de Documento
        b-col(md="6")
          b-form-group(:label="$t('declaration.tipoDocumento')", label-for="tipoDocumento")
            validation-provider(:name="$t('declaration.tipoDocumento')", rules="required", v-slot="{ errors }")
              v-select(
                v-model="data.tipoDocumento",
                :options="tipoDocumentoOptions",
                :reduce="opt => opt.value",
                label="title",
                :placeholder="$t('declaration.placeholder_tipo')"
              )
              small(class="text-danger") {{ errors[0] }}

        //- Temática (free text for Lobby, dropdown for Conflict of Interest)
        b-col(md="6")
          b-form-group(:label="$t('declaration.tematica')", label-for="tematica")
            b-form-input#tematica(
              v-if="config.tematicaFreeText",
              v-model="data.tematica",
              trim,
              :placeholder="$t('declaration.placeholder_tematica')"
            )
            v-select(
              v-else,
              v-model="data.tematica",
              :options="tematicaSelectOptions",
              :reduce="opt => opt.value",
              label="title",
              :placeholder="$t('declaration.placeholder_tematica')"
            )

        //- Responsable de Verificar
        b-col(md="6")
          b-form-group(:label="$t('declaration.responsable')", label-for="responsable")
            validation-provider(:name="$t('declaration.responsable')", rules="required", v-slot="{ errors }")
              v-select(
                v-model="data.responsable",
                :options="workersTotal",
                :reduce="worker => worker.value",
                :selectable="worker => !worker.deleted",
                label="title",
                :placeholder="$t('declaration.placeholder_responsable')"
              )
              small(class="text-danger") {{ errors[0] }}

        //- Empresa (Conflict of Interest: after Responsable)
        b-col(v-if="!config.showInstitucion", md="6")
          b-form-group(:label="$t('declaration.empresa')", label-for="empresa")
            v-select(
              v-model="data.empresas",
              :options="companies",
              :reduce="company => company.value",
              label="title",
              multiple,
              :placeholder="$t('declaration.placeholder_empresa')"
            )

        //- Fecha reunión (Lobby only)
        b-col(v-if="config.showFechaReunion", md="6")
          b-form-group(:label="$t('declaration.fechaReunion')", label-for="fechaReunion")
            b-form-datepicker(v-model="data.fechaReunion", :locale="locale", :placeholder="$t('declaration.placeholder_fecha')", @input="onFechaReunionChange")

        //- Próxima Revisión
        b-col(md="6")
          b-form-group(:label="$t('declaration.fechaProximaRevision')", label-for="fechaProximaRevision")
            b-form-datepicker(v-model="data.fechaProximaRevision", :locale="locale", :placeholder="$t('declaration.placeholder_fecha')")

      b-row
        //- Observaciones
        b-col(md="12")
          b-form-group(:label="$t('declaration.observaciones')", label-for="observaciones")
            b-form-textarea(
              v-model="data.observaciones",
              :placeholder="$t('declaration.placeholder_observaciones')",
              rows="3"
            )

      //- File upload (only for new documents)
      b-row(v-if="showFileUpload")
        b-col(md="6")
          b-form-group(:label="$t('label.document')", label-for="archivo")
            b-button.mr-1(type="button", variant="info", @click="activateFileSelector")
              feather-icon(icon="UploadIcon", class="mr-50")
              | {{ $t('message.upload') }}
            input(
              type="file",
              ref="fileSelector",
              style="display: none",
              :accept="inputFileTypes",
              @change="changeFileSelected"
            )
            small.ml-1(v-if="documentFile.name")
              feather-icon(icon="FileIcon", class="mr-25")
              | {{ documentFile.name }}
            small(v-else-if="fileRequired", class="text-danger d-block mt-50")
              | {{ $t('declaration.file_required') }}

      b-row.mt-2
        b-col.text-right(cols="12")
          b-button(variant="success", :disabled="isSubmitting", @click="validateAndSubmit")
            | {{ $t('message.submit') }}

      b-row(v-if="isSubmitting")
        b-col.text-right(cols="12")
          div.mt-1
            | {{ $t('declaration.uploading') }}

  //- Audit Log Table
  audit-log-table(
    v-if="isEdit && data"
    :history="data.history || []"
    :field-translations="{\
      nombre: $t('declaration.nombre'),\
      trabajador: $t('message.tableHeader.worker'),\
      institucion: $t('declaration.institucion'),\
      area: $t('message.tableHeader.location'),\
      tipoDocumento: $t('declaration.tipoDocumento'),\
      tematica: $t('declaration.tematica'),\
      responsable: $t('label.responsible'),\
      empresas: $t('label.company'),\
      observaciones: $t('label.observations'),\
      fechaReunion: $t('declaration.fechaReunion'),\
      fechaProximaRevision: $t('declaration.fechaProximaRevision')\
    }"
  )
</template>

<script>
import vSelect from 'vue-select'
import { ref, toRefs, onMounted, computed } from '@vue/composition-api/dist/vue-composition-api'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import useNotifications from '@/composables/useNotifications'
import AuditLogTable from '@/components/AuditLogTable.vue'
import i18n from '@/libs/i18n'
import { useRouter } from '@core/utils/utils'
import useCommon from '@/views/organization/useCommon'
import { inputFileTypes } from '@/constants'
import { getDeclarationConfig, tipoOptions, tematicaOptions } from '../declarationConfig'

export default {
  name: 'DeclarationForm',

  components: {
    vSelect,
    ValidationObserver,
    ValidationProvider,
    AuditLogTable,
  },

  props: {
    data: {
      type: Object,
      required: true,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    showFileUpload: {
      type: Boolean,
      default: true,
    },
    fileRequired: {
      type: Boolean,
      default: true,
    },
  },

  setup(props, { emit }) {
    const {
      getWorkersTotalForDropDown, workersTotal,
      getCompaniesForDropDown, companies,
      getLocationsForDropDown, locations,
    } = useCommon()
    const { showErrorMessage } = useNotifications()
    const { route } = useRouter()
    const config = getDeclarationConfig(route.value?.path)
    const { default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')
    const locale = default_language || 'es'
    const form = ref(null)
    const fileSelector = ref(null)
    const documentFile = ref({})

    const tipoDocumentoOptions = computed(() => tipoOptions.value[config.type] || [])
    const tematicaSelectOptions = computed(() => tematicaOptions.value)

    const activateFileSelector = () => {
      fileSelector.value.click()
    }

    const changeFileSelected = e => {
      documentFile.value = e.target.files[0]
    }

    // Lobby: Próxima Revisión defaults to the meeting date until edited manually.
    const onFechaReunionChange = value => {
      if (value && !props.data.fechaProximaRevision) {
        // eslint-disable-next-line no-param-reassign
        props.data.fechaProximaRevision = value
      }
    }

    const resolveDisplayName = () => {
      // Denormalize the main-entity display into `nombre` (used by list/view/emails).
      if (config.mainFieldKind === 'text') return
      const selected = workersTotal.value.find(w => w.value === props.data.trabajador)
      // eslint-disable-next-line no-param-reassign
      props.data.nombre = selected?.title || ''
    }

    const validateAndSubmit = () => {
      form.value.validate().then(success => {
        if (success) {
          if (props.fileRequired && props.showFileUpload && !documentFile.value.name) {
            showErrorMessage(i18n.t('declaration.file_required'))
            return
          }
          resolveDisplayName()
          emit('submit', toRefs(props.data), documentFile.value)
        } else {
          showErrorMessage(i18n.t('message.requiredFieldsIncomplete'))
        }
      })
    }

    onMounted(() => {
      getWorkersTotalForDropDown()
      getCompaniesForDropDown()
      getLocationsForDropDown()
    })

    return {
      required,
      config,
      form,
      fileSelector,
      documentFile,
      activateFileSelector,
      changeFileSelected,
      onFechaReunionChange,
      validateAndSubmit,
      tipoDocumentoOptions,
      tematicaSelectOptions,
      workersTotal,
      companies,
      locations,
      inputFileTypes,
      locale,
    }
  },
}
</script>
